export const CHECKLIST_TYPE = 'editable_checklist'

export type ChecklistOptionField = {
  name?: string
  type?: string
  param_key: string
  value?: string | number
  hidden?: boolean
  editable?: boolean
}

export type ChecklistOption = {
  name?: string
  description?: string
  fields: Array<ChecklistOptionField>
}

export type ChecklistField = {
  name?: string
  type: typeof CHECKLIST_TYPE
  param_key: string
  required?: boolean
  options: Array<ChecklistOption>
  currency?: string
  available_balance?: string
}

export type ChecklistEntry = Record<string, string>
export type ChecklistRow = { checked: boolean; values: ChecklistEntry }

export const AMOUNT_PARAM_KEY = 'amount'
export const DEFAULT_CURRENCY = 'USD'
export const AMOUNT_DECIMALS = 2

export const isChecklistField = (field: { type: string }): field is ChecklistField => field.type === CHECKLIST_TYPE

const toNumber = (value?: unknown) => {
  const parsed = Number(value)

  return Number.isFinite(parsed) && value !== '' && value !== null ? parsed : undefined
}

const toText = (value?: string | number) => (typeof value === 'undefined' ? '' : String(value))

export const parseAmount = (value?: unknown) => toNumber(value) ?? 0

export const getEditableFields = (option: ChecklistOption) => option.fields.filter((f) => f.editable && !f.hidden)

export const getAmountField = (option: ChecklistOption) => {
  const editable = getEditableFields(option)

  return editable.find((f) => f.type === 'number') ?? editable.find((f) => f.param_key === AMOUNT_PARAM_KEY)
}

const getAmountKey = (option?: ChecklistOption) => (option && getAmountField(option)?.param_key) || AMOUNT_PARAM_KEY

export const getCurrency = (paramsField: ChecklistField) => paramsField.currency ?? DEFAULT_CURRENCY

export const getAvailableBalance = (paramsField: ChecklistField) => toNumber(paramsField.available_balance)

export const getRowMax = (option: ChecklistOption) => toNumber(getAmountField(option)?.value)

export const buildRow = (option: ChecklistOption): ChecklistRow => ({
  checked: false,
  values: option.fields.reduce<ChecklistEntry>((acc, f) => {
    acc[f.param_key] = toText(f.value)

    return acc
  }, {}),
})

export const toEntries = (rows: Array<ChecklistRow>) => rows.filter((r) => r.checked).map((r) => r.values)

export const isRowOverMax = (option: ChecklistOption, row: ChecklistRow) => {
  const max = getRowMax(option)

  return row.checked && typeof max === 'number' && parseAmount(row.values[getAmountKey(option)]) > max
}

export const sumCheckedRows = (options: Array<ChecklistOption>, rows: Array<ChecklistRow>) =>
  rows.reduce((sum, row, index) => {
    const option = options[index]

    return row.checked && option ? sum + parseAmount(row.values[getAmountKey(option)]) : sum
  }, 0)

export const hasSubCentPrecision = (value: string) => (value.split('.')[1]?.length ?? 0) > AMOUNT_DECIMALS

const toCents = (amount: number) => Math.round(amount * 100)

const findOption = (paramsField: ChecklistField, entry: ChecklistEntry) =>
  paramsField.options.find((option) => {
    const hidden = option.fields.filter((f) => f.hidden)

    return hidden.length > 0 && hidden.every((f) => entry[f.param_key] === toText(f.value))
  })

export const validateChecklist =
  (paramsField: ChecklistField) => (value: unknown, formValues?: Record<string, unknown>) => {
    const entries: Array<ChecklistEntry> = Array.isArray(value) ? value : []
    let total = parseAmount(formValues?.[AMOUNT_PARAM_KEY])

    for (const entry of entries) {
      const option = findOption(paramsField, entry)
      const raw = entry[getAmountKey(option)] ?? ''
      const amount = parseAmount(raw)

      if (hasSubCentPrecision(raw)) return `Amounts are limited to ${AMOUNT_DECIMALS} decimals`

      const max = option && getRowMax(option)

      if (typeof max === 'number' && amount > max) return `Amount exceeds the ${max} allowed on a checked row`

      total += amount
    }

    const balance = getAvailableBalance(paramsField)

    if (typeof balance !== 'undefined' && toCents(total) > toCents(balance)) {
      return 'Total exceeds the available balance'
    }

    return true
  }
